<script lang="ts">
	import { onNavigate } from '$app/navigation';
	import { page } from '$app/stores';
	import { translations } from '$lib/i18n/translations';
	import type { Property } from '$lib/satellite.types';

	const property = $page.data.property as Property;
	const colors = property.colors;
	const t = translations(property.language);

	export let opened = false;
	export let searchFocus = false;

	let timer: ReturnType<typeof setTimeout>;

	const close = () => {
		opened = false;
		document.body.classList.remove('hasMenuOpen');
	};

	const open = () => {
		opened = true;
		document.body.classList.add('hasMenuOpen');
	};

	const toggle = () => {
		opened ? close() : open();
	};

	const searchFocusIn = () => {
		clearTimeout(timer);
		searchFocus = true;
	};

	const searchFocusOut = () => {
		timer = setTimeout(() => {
			searchFocus = false;
		}, 300);
	};

	$: currentCategory = $page.data.article?.category?.slug || $page.data.category?.slug;
	$: searchTerm = $page.data?.query ?? '';

	onNavigate(() => {
		close();
	});
</script>

<div
	class="backdrop-blur shadow-lg lg:hidden fixed top-0 h-full w-10/12 bg-white/90 z-50 -left-full transition-all duration-300 ease-in-out px-4"
	class:!left-0={opened}
>
	<div class="py-6">
		<a href="/">
			<img
				src="/logo.svg"
				loading="eager"
				fetchpriority="high"
				alt={property.name}
				class={`h-10 ${property.aspectRatio}`}
			/>
		</a>
	</div>

	<ul class="space-y-5">
		{#each property.categories as category}
			{#if !category.hidden}
				<li>
					<a
						href={`/${category.slug}`}
						class={`${currentCategory == category.slug ? colors.hover + ' ' : null}text-gray-500 group uppercase font-medium tracking-wider text-xl transition-colors duration-300 hover:${colors.hover}`}
					>
						{category.name}
						<span
							class={`${currentCategory == category.slug ? 'max-w-full ' + colors.hoverBackground : 'max-w-0 ' + colors.background} block group-hover:max-w-full transition-all duration-300 h-0.5 group-hover:${colors.hoverBackground}`}
						></span>
					</a>
				</li>
			{/if}
		{/each}
	</ul>

	<div class="mt-3">
		<form method="get" action={`/${property.language == 'fr' ? 'recherche' : 'search'}`}>
			<div class="relative rounded-md shadow-sm">
				<input
					name="q"
					type="text"
					class={`block w-full rounded-3xl border-0 py-1.5 pr-10 text-gray-900 ring-1 focus:ring-2 ring-inset outline-none ring-gray-300 placeholder:text-gray-400 focus:${colors.ring}`}
					placeholder={t('search')}
					value={searchTerm}
				/>
				<div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 20 20"
						fill="currentColor"
						class="w-5 h-5"
					>
						<path
							fill-rule="evenodd"
							d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
							clip-rule="evenodd"
						/>
					</svg>
				</div>
			</div>
		</form>
	</div>
</div>

<nav class="bg-white w-full [view-transition-name:navbar] z-50">
	<div class="max-w-screen-lg px-4 xl:px-0 mx-auto py-6">
		<div class="w-full flex items-center">
			<a href="/">
				<img src="/logo.svg" alt={property.name} class={`h-10 ${property.aspectRatio}`} />
			</a>
			<div class="hidden lg:flex grow">
				<div class="border-l border-gray-300 ml-4 pl-3.5 pointer-events-none"></div>
				<ul
					class="flex space-x-4 items-center overflow-hidden"
					class:w-0={searchFocus}
					class:h-0={searchFocus}
				>
					{#each property.categories as category}
						{#if !category.hidden}
							<li>
								<a
									href={`/${category.slug}`}
									class={`${currentCategory == category.slug ? colors.hover + ' ' : null}text-gray-500 group uppercase font-medium tracking-wider text-sm transition-colors duration-300 hover:${colors.hover}`}
								>
									{category.name}
									<span
										class={`${currentCategory == category.slug ? 'max-w-full ' + colors.hoverBackground : 'max-w-0 ' + colors.background} block group-hover:max-w-full transition-all duration-300 h-0.5 group-hover:${colors.hoverBackground}`}
									></span>
								</a>
							</li>
						{/if}
					{/each}
				</ul>
				<div class="ml-auto">
					<form method="get" action={`/${property.language == 'fr' ? 'recherche' : 'search'}`}>
						<div class="relative rounded-md shadow-sm">
							<input
								name="q"
								type="text"
								on:focusin={searchFocusIn}
								on:focusout={searchFocusOut}
								class={`transition-all duration-300 focus:w-96 focus:pr-10 block w-0 overflow-hidden rounded-3xl border-0 py-1.5 pr-8 text-gray-900 ring-1 focus:ring-2 ring-inset outline-none ring-gray-300 placeholder:text-gray-400 text-sm focus:${colors.ring}`}
								placeholder={t('search')}
								value={searchTerm}
							/>
							<div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 20 20"
									fill="currentColor"
									class="w-5 h-5"
								>
									<path
										fill-rule="evenodd"
										d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
										clip-rule="evenodd"
									/>
								</svg>
							</div>
						</div>
					</form>
				</div>
			</div>
			<div class="lg:hidden ml-auto">
				<button class="hamburger hamburger--spin static" on:click={toggle} class:is-active={opened}>
					<div class="hamburger-box">
						<div class="hamburger-inner"></div>
					</div>
				</button>
			</div>
		</div>
	</div>
</nav>

<style lang="postcss">
	:global(body.hasMenuOpen) {
		@apply fixed overflow-y-scroll lg:static lg:overflow-y-auto;
	}

	:global(body.hasMenuOpen #content) {
		@apply pointer-events-none lg:pointer-events-auto;
	}

	.hamburger {
		display: inline-block;
		cursor: pointer;
		transition-property: opacity, filter;
		transition-duration: 0.15s;
		transition-timing-function: linear;
		font: inherit;
		color: inherit;
		text-transform: none;
		background-color: transparent;
		border: 0;
		margin: 0;
		overflow: visible;
	}
	.hamburger:hover {
		opacity: 0.7;
	}
	.hamburger.is-active:hover {
		opacity: 0.7;
	}
	.hamburger.is-active .hamburger-inner,
	.hamburger.is-active .hamburger-inner::before,
	.hamburger.is-active .hamburger-inner::after {
		background-color: #000;
	}

	.hamburger-box {
		width: 40px;
		height: 24px;
		display: inline-block;
		position: relative;
		vertical-align: middle;
	}

	.hamburger-inner {
		display: block;
		top: 50%;
		margin-top: -2px;
	}
	.hamburger-inner,
	.hamburger-inner::before,
	.hamburger-inner::after {
		width: 40px;
		height: 4px;
		background-color: #000;
		border-radius: 4px;
		position: absolute;
		transition-property: transform;
		transition-duration: 0.15s;
		transition-timing-function: ease;
	}
	.hamburger-inner::before,
	.hamburger-inner::after {
		content: '';
		display: block;
	}
	.hamburger-inner::before {
		top: -10px;
	}
	.hamburger-inner::after {
		bottom: -10px;
	}
	.hamburger--spin .hamburger-inner {
		transition-duration: 0.22s;
		transition-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);
	}
	.hamburger--spin .hamburger-inner::before {
		transition:
			top 0.1s 0.25s ease-in,
			opacity 0.1s ease-in;
	}
	.hamburger--spin .hamburger-inner::after {
		transition:
			bottom 0.1s 0.25s ease-in,
			transform 0.22s cubic-bezier(0.55, 0.055, 0.675, 0.19);
	}

	.hamburger--spin.is-active .hamburger-inner {
		transform: rotate(225deg);
		transition-delay: 0.12s;
		transition-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
	}
	.hamburger--spin.is-active .hamburger-inner::before {
		top: 0;
		opacity: 0;
		transition:
			top 0.1s ease-out,
			opacity 0.1s 0.12s ease-out;
	}
	.hamburger--spin.is-active .hamburger-inner::after {
		bottom: 0;
		transform: rotate(-90deg);
		transition:
			bottom 0.1s ease-out,
			transform 0.22s 0.12s cubic-bezier(0.215, 0.61, 0.355, 1);
	}
</style>
